import React, { Component } from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import OnDemandProductSearchField from "./OnDemandProductSearchField";
import commons from "../common/commons";

function getItemComponents(item) {
  if (Array.isArray(item?.componentsAtCheckout) && item.componentsAtCheckout.length > 0) {
    return item.componentsAtCheckout;
  }
  if (typeof item?.ean === "string" && item.ean.startsWith("GSET-")) {
    const parts = item.ean.replace(/^GSET-/, "").split("-");
    if (parts.length >= 2) {
      return [
        { position: "front", ean: parts[0] },
        { position: "rear", ean: parts.slice(1).join("-") },
      ];
    }
  }
  return [];
}

function getItemImageUrl(item) {
  const components = getItemComponents(item);
  const fallbackEan =
    components.find((component) => component.position === "rear")?.ean ||
    components.find((component) => component.ean)?.ean ||
    item.ean;
  const isGeorgiaItem = item.source === "georgia" || Boolean(item.supplierAtCheckout);
  return commons.getProductPrimaryImage(item.imageUrl || false, fallbackEan, isGeorgiaItem).url;
}

class EditOnDemandItemsDialog extends Component {
  state = {
    drafts: {},
    saving: false,
    error: null,
  };

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({ drafts: {}, saving: false, error: null });
    }
  }

  setDraft = (itemIndex, field, value) => {
    this.setState((prev) => ({
      drafts: {
        ...prev.drafts,
        [itemIndex]: {
          ...(prev.drafts[itemIndex] || {}),
          [field]: value,
        },
      },
      error: null,
    }));
  };

  buildReplacements = () => {
    const items = this.props.order?.onDemand?.items || [];
    const replacements = [];

    items.forEach((item, itemIndex) => {
      const draft = this.state.drafts[itemIndex] || {};
      if (draft.product?.ean) {
        replacements.push({ itemIndex, ean: draft.product.ean });
        return;
      }
      const payload = { itemIndex };
      if (draft.front?.ean) payload.frontEan = draft.front.ean;
      if (draft.rear?.ean) payload.rearEan = draft.rear.ean;
      if (payload.frontEan || payload.rearEan) {
        replacements.push(payload);
      }
    });

    return replacements;
  };

  handleSave = () => {
    const replacements = this.buildReplacements();
    if (!replacements.length) {
      this.setState({ error: "בחרו לפחות מוצר אחד להחלפה" });
      return;
    }

    this.setState({ saving: true, error: null });
    this.props
      .onSave(replacements)
      .then(() => {
        this.setState({ saving: false });
      })
      .catch((error) => {
        this.setState({
          saving: false,
          error: error?.message || "שגיאה בעדכון המוצרים",
        });
      });
  };

  renderItem(item, itemIndex) {
    const draft = this.state.drafts[itemIndex] || {};
    const components = getItemComponents(item);
    const imageUrl = getItemImageUrl(item);
    const title = [item.brand, item.displayName || item.ean].filter(Boolean).join(" ");
    const hasWholeReplacement = Boolean(draft.product);

    return (
      <Paper
        key={`${item.ean}-${itemIndex}`}
        elevation={0}
        style={{
          padding: 16,
          marginBottom: 16,
          border: "1px solid #e5e7eb",
          borderRadius: 8,
        }}
      >
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          {imageUrl && (
            <div
              style={{
                width: 72,
                height: 72,
                flexShrink: 0,
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                background: `#f9fafb url(${imageUrl}) center / contain no-repeat`,
              }}
            />
          )}
          <div>
            <Typography style={{ fontWeight: 700 }}>
              {title} {item.displaySize || ""}
            </Typography>
            <Typography variant="body2" style={{ color: "#6b7280", fontFamily: "monospace" }}>
              EAN: {item.ean}
            </Typography>
            {components.map((component) => (
              <Typography
                key={`${component.position}-${component.ean}`}
                variant="body2"
                style={{ color: "#4b5563", fontFamily: "monospace" }}
              >
                {component.position}: {component.ean}
              </Typography>
            ))}
            <Typography variant="caption" style={{ color: "#2563eb", display: "block", marginTop: 4 }}>
              המחיר נשמר כפי שהוא בהזמנה
            </Typography>
          </div>
        </div>

        <OnDemandProductSearchField
          label="החלף סט / צמיג שלם"
          value={draft.product || null}
          onChange={(product) => this.setDraft(itemIndex, "product", product)}
        />

        {components.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <OnDemandProductSearchField
              label="החלף צמיג קדמי בלבד"
              value={draft.front || null}
              disabled={hasWholeReplacement}
              onChange={(product) => this.setDraft(itemIndex, "front", product)}
            />
            <OnDemandProductSearchField
              label="החלף צמיג אחורי בלבד"
              value={draft.rear || null}
              disabled={hasWholeReplacement}
              onChange={(product) => this.setDraft(itemIndex, "rear", product)}
            />
          </div>
        )}
      </Paper>
    );
  }

  render() {
    const { open, order, onClose } = this.props;
    const { saving, error } = this.state;
    const items = order?.onDemand?.items || [];

    return (
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>עדכון מוצרים בהזמנה #{order?.orderId}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ marginBottom: 16, color: "#4b5563" }}>
            בחרו צמיג או סט חלופי. התמונה וה־EAN יתעדכנו אצל הלקוח, בלי לשנות את המחיר ששולם.
          </Typography>
          {items.map((item, index) => this.renderItem(item, index))}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            ביטול
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={this.handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={18} /> : "שמור שינוי מוצרים"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

EditOnDemandItemsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditOnDemandItemsDialog;
